/**
 * @file 업무 캘린더 API
 * 캘린더 일정의 조회·생성·수정·삭제, 첨부파일 관리, 직원/부서 목록 조회,
 * 휴가 일정 조회 등을 처리합니다.
 */

import { api } from "../../../util/api";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const calendarApi = {
    /** 캘린더 일정 목록 조회
     * @param {string} from - 조회 시작일 (YYYY-MM-DD)
     * @param {string} to - 조회 종료일 (YYYY-MM-DD)
     * @param {number[]} [employeeIds] - 필터링할 직원 ID 배열
     * @param {number[]} [deptIds] - 필터링할 부서 ID 배열
     */
    getEvents: (from, to, employeeIds, deptIds) => {
        const params = { from, to };
        if (employeeIds?.length) params.employeeIds = employeeIds.join(",");
        if (deptIds?.length)     params.deptIds     = deptIds.join(",");
        return api.get(`${API_BASE}/work-calendar/events`, params);
    },

    /** 캘린더 일정 생성
     * @param {object} data - 일정 데이터
     */
    create: (data)     => api.post(`${API_BASE}/work-calendar/events`, data),

    /** 캘린더 일정 수정
     * @param {number} id - 일정 ID
     * @param {object} data - 수정할 일정 데이터
     */
    update: (id, data) => api.put(`${API_BASE}/work-calendar/events/${id}`, data),

    /** 캘린더 일정 삭제
     * @param {number} id - 일정 ID
     */
    delete: (id)       => api.delete(`${API_BASE}/work-calendar/events/${id}`),

    /** 일정 첨부파일 목록 조회
     * @param {number} eventId - 일정 ID
     */
    getAttachments:   (eventId)       => api.get(`${API_BASE}/work-calendar/events/${eventId}/attachments`),

    /** 일정 첨부파일 업로드 (multipart/form-data)
     * @param {number} eventId - 일정 ID
     * @param {File} file - 업로드할 파일
     */
    uploadAttachment: (eventId, file) => {
        const form = new FormData();
        form.append("file", file);
        return api.upload(`${API_BASE}/work-calendar/events/${eventId}/attachments`, form);
    },

    /** 일정 첨부파일 삭제
     * @param {number} attachId - 첨부파일 ID
     */
    deleteAttachment: (attachId) => api.delete(`${API_BASE}/work-calendar/attachments/${attachId}`),

    /** 첨부 미리보기 (blob). PDF·이미지·엑셀·텍스트는 원본, 오피스는 서버 PDF 변환. */
    previewAttachment: (attachId) => api.download(`${API_BASE}/work-calendar/attachments/${attachId}/preview`),

    /** 전체 직원 목록 조회 */
    getEmployees:  () => api.get(`${API_BASE}/employees`),

    /** 부서 목록 조회 */
    getDepartments: () => api.get(`${API_BASE}/employees/departments`),

    /** 휴가 일정 목록 조회
     * @param {string} from - 조회 시작일 (YYYY-MM-DD)
     * @param {string} to - 조회 종료일 (YYYY-MM-DD)
     * @param {number[]} [employeeIds] - 필터링할 직원 ID 배열
     * @param {number[]} [deptIds] - 필터링할 부서 ID 배열
     * @param {string[]} [leaveTypes] - 필터링할 휴가 유형 배열
     */
    getLeaveEvents: (from, to, employeeIds, deptIds, leaveTypes) => {
        const params = { from, to };
        if (employeeIds?.length) params.employeeIds = employeeIds.join(",");
        if (deptIds?.length)     params.deptIds     = deptIds.join(",");
        if (leaveTypes?.length)  params.leaveTypes  = leaveTypes.join(",");
        return api.get(`${API_BASE}/approval/leaves`, params);
    },

    /** 프로젝트 채널 일정 목록 조회 — 휴가와 동일한 SSOT(읽기 시점 라이브 조회). 채널을 복사 저장하지 않아
     *  채널명·기간 수정이 즉시 반영되고 채널 삭제 시 자동으로 사라진다.
     * @param {string} from - 조회 시작일 (YYYY-MM-DD)
     * @param {string} to - 조회 종료일 (YYYY-MM-DD)
     */
    getChannelEvents: (from, to) => api.get(`${API_BASE}/channel/calendar-events`, { from, to }),
};
